/**
 * Feature Flags Configuration
 *
 * Controls which optional features are enabled in the application.
 * Features can be enabled/disabled via environment variables.
 * This allows deployment flexibility - not all services need to be running.
 *
 * IMPORTANT: Next.js only inlines NEXT_PUBLIC_* env vars when accessed with
 * literal strings (e.g., process.env.NEXT_PUBLIC_FEATURE_PHOENIX).
 * Dynamic access like process.env[variable] won't work at build time.
 */

// Helper to check if an env value is truthy
function isTruthy(value: string | undefined, defaultEnabled: boolean): boolean {
  if (value === undefined || value === '') {
    return defaultEnabled;
  }
  const normalized = value.toLowerCase().trim();
  return normalized === 'true' || normalized === '1' || normalized === 'enabled';
}

// Feature flag definitions with metadata
const FEATURE_FLAGS = {
  horizonAgents: {
    envVar: 'FEATURE_HORIZON_AGENTS',
    defaultEnabled: false,
    description: 'Horizon goal-driven agents, delegation and durable execution',
  },
  knowledgeAgents: {
    envVar: 'FEATURE_KNOWLEDGE_AGENTS',
    defaultEnabled: false,
    description: 'Knowledge preparation, native RAGFlow indexing, graph compilation and evidence evaluation',
  },
  analyticsAgents: {
    envVar: 'FEATURE_ANALYTICS_AGENTS',
    defaultEnabled: false,
    description: 'Analytics agent authoring, transcript preparation and evidence-backed chat',
  },
  sceneAgents: {
    envVar: 'FEATURE_SCENE_AGENTS',
    defaultEnabled: false,
    description: 'Scene agents, Director authoring and recording exports',
  },
  firecrawl: {
    envVar: 'NEXT_PUBLIC_FEATURE_FIRECRAWL',
    defaultEnabled: false,
    description: 'FireCrawl web scraping integration',
  },
  scaleTesting: {
    envVar: 'NEXT_PUBLIC_FEATURE_SCALE_TESTING',
    defaultEnabled: true,
    description: 'Scale testing and simulation',
  },
  a2ui: {
    envVar: 'NEXT_PUBLIC_FEATURE_A2UI',
    defaultEnabled: true,
    description: 'A2UI catalogs, multimodality settings, and catalog workspace',
  },
  agents: {
    envVar: 'NEXT_PUBLIC_FEATURE_AGENTS',
    defaultEnabled: true,
    description: 'Real-time agent authoring, dashboard, and agent testing (ai-orchestrator)',
  },
  workflows: {
    envVar: 'NEXT_PUBLIC_FEATURE_WORKFLOWS',
    defaultEnabled: true,
    description: 'Async workflow authoring and runtime (async-agent)',
  },
  costEstimates: {
    envVar: 'NEXT_PUBLIC_FEATURE_COST_ESTIMATES',
    defaultEnabled: false,
    description: 'Provider-cost forecasts, measured usage, and costing administration',
  },
  sip: {
    envVar: 'NEXT_PUBLIC_FEATURE_SIP',
    defaultEnabled: true,
    description: 'SIP telephony stack (Asterisk/Kamailio/rtpengine/Janus) settings and tools',
  },
  swimlanes: {
    envVar: 'NEXT_PUBLIC_FEATURE_SWIMLANES',
    defaultEnabled: false,
    description: 'Multi-swimlane pipeline authoring (parallel S2S agents, cross-feeds, turn arbitration)',
  },
  aiGeneration: {
    envVar: 'NEXT_PUBLIC_FEATURE_AI_GENERATION',
    defaultEnabled: false,
    description: 'Generate with AI: CLI-agent-powered agent/workflow generation (workspace-agent-runner)',
  },
  procedures: {
    envVar: 'NEXT_PUBLIC_FEATURE_PROCEDURES',
    defaultEnabled: true,
    description: 'Procedure supervision authoring (step-card editor + Preview checklist); the runtime engages purely on config presence',
  },
  teams: {
    envVar: 'NEXT_PUBLIC_FEATURE_TEAMS',
    defaultEnabled: false,
    description: 'Microsoft Teams agent exposure (settings panel + package download); hidden until ready to test',
  },
  avatar: {
    envVar: 'NEXT_PUBLIC_FEATURE_AVATAR',
    defaultEnabled: false,
    description: 'Avatar (Azure Avatar / Bey) settings + tester avatar view — rarely used, off by default',
  },
  realSmsTesting: {
    envVar: 'NEXT_PUBLIC_FEATURE_REAL_SMS_TESTING',
    defaultEnabled: false,
    description: 'SMS in full-screen agent Preview for sending test messages to the caller ANI',
  },
  platformAdmin: {
    envVar: 'NEXT_PUBLIC_FEATURE_PLATFORM_ADMIN',
    defaultEnabled: true,
    description: 'Platform Admin console (super-admin only): manage tenants, users, memberships/roles, and platform super-admins, with write-back to Authentik groups. Defaults ON — it replaces the always-on platform-admin tenant management; set false as a kill-switch.',
  },
} as const;

export type FeatureFlag = keyof typeof FEATURE_FLAGS;

/**
 * Check if a feature is enabled based on environment variable.
 * Uses literal string access for Next.js build-time inlining.
 * Scene/Analytics are runtime-only: client components must use the
 * StudioFeaturesProvider hooks instead of checking those two flags here.
 */
export function isFeatureEnabled(feature: FeatureFlag): boolean {
  // Must use literal string access for Next.js to inline NEXT_PUBLIC_* vars
  switch (feature) {
    case 'horizonAgents':
      return isHorizonAgentsEnabledServer();
    case 'knowledgeAgents':
      return isKnowledgeAgentsEnabledServer();
    case 'analyticsAgents':
      return isAnalyticsAgentsEnabledServer();
    case 'sceneAgents':
      return isSceneAgentsEnabledServer();
    case 'firecrawl':
      return isTruthy(process.env.NEXT_PUBLIC_FEATURE_FIRECRAWL, false);
    case 'scaleTesting':
      return isTruthy(process.env.NEXT_PUBLIC_FEATURE_SCALE_TESTING, true);
    case 'a2ui':
      return isTruthy(process.env.NEXT_PUBLIC_FEATURE_A2UI, true);
    case 'agents':
      return isTruthy(process.env.NEXT_PUBLIC_FEATURE_AGENTS, true);
    case 'workflows':
      return isTruthy(process.env.NEXT_PUBLIC_FEATURE_WORKFLOWS, true);
    case 'costEstimates':
      return isTruthy(process.env.NEXT_PUBLIC_FEATURE_COST_ESTIMATES, false);
    case 'sip':
      return isTruthy(process.env.NEXT_PUBLIC_FEATURE_SIP, true);
    case 'swimlanes':
      return isTruthy(process.env.NEXT_PUBLIC_FEATURE_SWIMLANES, false);
    case 'aiGeneration':
      return isTruthy(process.env.NEXT_PUBLIC_FEATURE_AI_GENERATION, false);
    case 'procedures':
      return isTruthy(process.env.NEXT_PUBLIC_FEATURE_PROCEDURES, true);
    case 'teams':
      return isTruthy(process.env.NEXT_PUBLIC_FEATURE_TEAMS, false);
    case 'avatar':
      return isTruthy(process.env.NEXT_PUBLIC_FEATURE_AVATAR, false);
    case 'realSmsTesting':
      return isTruthy(process.env.NEXT_PUBLIC_FEATURE_REAL_SMS_TESTING, false);
    case 'platformAdmin':
      return isTruthy(process.env.NEXT_PUBLIC_FEATURE_PLATFORM_ADMIN, true);
    default:
      return false;
  }
}

/**
 * Server-side A2UI feature check. Accepts either the NEXT_PUBLIC_* var (baked
 * at build time) or a plain FEATURE_A2UI runtime override so API routes can be
 * toggled without rebuilding the image.
 */
export function isA2uiEnabledServer(): boolean {
  const runtime = process.env.FEATURE_A2UI;
  if (runtime !== undefined && runtime !== '') {
    return isTruthy(runtime, true);
  }
  return isTruthy(process.env.NEXT_PUBLIC_FEATURE_A2UI, true);
}

/**
 * Server-side capability checks for modular deployments. Each accepts a plain
 * FEATURE_* runtime override (so API routes / proxy can toggle without a
 * rebuild) and falls back to the NEXT_PUBLIC_* var baked at build time.
 */
export function isAgentsEnabledServer(): boolean {
  const runtime = process.env.FEATURE_AGENTS;
  if (runtime !== undefined && runtime !== '') {
    return isTruthy(runtime, true);
  }
  return isTruthy(process.env.NEXT_PUBLIC_FEATURE_AGENTS, true);
}

export function isAnalyticsAgentsEnabledServer(): boolean {
  // Runtime-only opt-ins must not follow an image's baked NEXT_PUBLIC_* values
  // into another deployment. Client UI receives these through StudioFeaturesProvider.
  return isAgentsEnabledServer() && isTruthy(process.env.FEATURE_ANALYTICS_AGENTS, false);
}

export function isKnowledgeAgentsEnabledServer(): boolean {
  return isAgentsEnabledServer() && isTruthy(process.env.FEATURE_KNOWLEDGE_AGENTS, false);
}

export function isHorizonAgentsEnabledServer(): boolean {
  return isAgentsEnabledServer() && isTruthy(process.env.FEATURE_HORIZON_AGENTS, false);
}

/** UI opt-in only: saved configuration and runtime execution remain available. */
export function isStudioExtensionsEnabledServer(): boolean {
  return isTruthy(process.env.FEATURE_STUDIO_EXTENSIONS, false);
}

/**
 * Preview's Debug footer alone, without the rest of the Studio extensions.
 * `FEATURE_STUDIO_EXTENSIONS` reveals Debug among a large bundle (telephony
 * Preview, Demo Mode, Preview links, test-input editors, publication
 * controls), so there was no way to get the debugger without all of it.
 * Extensions still implies Debug; this flag only widens, never restricts.
 */
export function isPreviewDebugEnabledServer(): boolean {
  return isStudioExtensionsEnabledServer() || isTruthy(process.env.FEATURE_PREVIEW_DEBUG, false);
}

export function isSceneAgentsEnabledServer(): boolean {
  return isAgentsEnabledServer() && isTruthy(process.env.FEATURE_SCENE_AGENTS, false);
}

export function isAgentSubtypeEnabledServer(subtype: string | undefined): boolean {
  if (subtype === 'horizon_agents') return isHorizonAgentsEnabledServer();
  if (subtype === 'knowledge_management_agents') return isKnowledgeAgentsEnabledServer();
  if (subtype === 'analytics_agents') return isAnalyticsAgentsEnabledServer();
  if (subtype === 'scene_agents') return isSceneAgentsEnabledServer();
  return true;
}

export function isOptionalAgentRouteEnabledServer(pathname: string): boolean {
  if (pathname === '/api/horizon-agents' || pathname.startsWith('/api/horizon-agents/') ||
    /^\/agents\/[^/]+\/horizon(?:\/|$)/.test(pathname)) {
    return isHorizonAgentsEnabledServer();
  }
  if (pathname === '/api/knowledge-agents' || pathname.startsWith('/api/knowledge-agents/') ||
    /^\/agents\/[^/]+\/knowledge(?:\/|$)/.test(pathname)) {
    return isKnowledgeAgentsEnabledServer();
  }
  if (pathname === '/api/director' || pathname.startsWith('/api/director/') || /^\/agents\/[^/]+\/director(?:\/|$)/.test(pathname) ||
    pathname === '/api/scene-stages' || pathname.startsWith('/api/scene-stages/') || pathname === '/scene-stage-preview') {
    return isSceneAgentsEnabledServer();
  }
  if (pathname === '/api/analytics' || pathname.startsWith('/api/analytics/') ||
    pathname === '/analytics' || pathname.startsWith('/analytics/') || /^\/agents\/[^/]+\/analytics(?:\/|$)/.test(pathname)) {
    return isAnalyticsAgentsEnabledServer();
  }
  return true;
}

export function isWorkflowsEnabledServer(): boolean {
  const runtime = process.env.FEATURE_WORKFLOWS;
  if (runtime !== undefined && runtime !== '') {
    return isTruthy(runtime, true);
  }
  return isTruthy(process.env.NEXT_PUBLIC_FEATURE_WORKFLOWS, true);
}

export function isCostEstimatesEnabledServer(): boolean {
  const runtime = process.env.FEATURE_COST_ESTIMATES;
  if (runtime !== undefined && runtime !== '') {
    return isTruthy(runtime, false);
  }
  return isTruthy(process.env.NEXT_PUBLIC_FEATURE_COST_ESTIMATES, false);
}

export function isSipEnabledServer(): boolean {
  const runtime = process.env.FEATURE_SIP;
  if (runtime !== undefined && runtime !== '') {
    return isTruthy(runtime, true);
  }
  return isTruthy(process.env.NEXT_PUBLIC_FEATURE_SIP, true);
}

export function isAiGenerationEnabledServer(): boolean {
  const runtime = process.env.FEATURE_AI_GENERATION;
  if (runtime !== undefined && runtime !== '') {
    return isTruthy(runtime, false);
  }
  return isTruthy(process.env.NEXT_PUBLIC_FEATURE_AI_GENERATION, false);
}

export function isTeamsEnabledServer(): boolean {
  const runtime = process.env.FEATURE_TEAMS;
  if (runtime !== undefined && runtime !== '') {
    return isTruthy(runtime, false);
  }
  return isTruthy(process.env.NEXT_PUBLIC_FEATURE_TEAMS, false);
}

export function isPlatformAdminEnabledServer(): boolean {
  const runtime = process.env.FEATURE_PLATFORM_ADMIN;
  if (runtime !== undefined && runtime !== '') {
    return isTruthy(runtime, true);
  }
  return isTruthy(process.env.NEXT_PUBLIC_FEATURE_PLATFORM_ADMIN, true);
}

export function isStrandsExportEnabledServer(): boolean {
  const runtime = process.env.ENABLE_STRANDS_EXPORT;
  if (runtime !== undefined && runtime !== '') {
    return isTruthy(runtime, false);
  }
  return isTruthy(process.env.NEXT_PUBLIC_ENABLE_STRANDS_EXPORT, false);
}

export function isCodeExportEnabledServer(): boolean {
  const runtime = process.env.ENABLE_CODE_EXPORT;
  if (runtime !== undefined && runtime !== '') {
    return isTruthy(runtime, false);
  }
  return isTruthy(process.env.NEXT_PUBLIC_ENABLE_CODE_EXPORT, false);
}

/**
 * Get all enabled features as an array.
 */
export function getEnabledFeatures(): FeatureFlag[] {
  return (Object.keys(FEATURE_FLAGS) as FeatureFlag[]).filter(isFeatureEnabled);
}

/**
 * Get feature configuration info (for debugging/admin UI).
 */
export function getFeatureInfo(feature: FeatureFlag): {
  enabled: boolean;
  description: string;
  envVar: string;
} {
  const config = FEATURE_FLAGS[feature];
  return {
    enabled: isFeatureEnabled(feature),
    description: config.description,
    envVar: config.envVar,
  };
}

/**
 * Get all feature configurations (for debugging/admin UI).
 */
export function getAllFeatureInfo(): Record<
  FeatureFlag,
  { enabled: boolean; description: string; envVar: string }
> {
  const result = {} as Record<
    FeatureFlag,
    { enabled: boolean; description: string; envVar: string }
  >;

  for (const feature of Object.keys(FEATURE_FLAGS) as FeatureFlag[]) {
    result[feature] = getFeatureInfo(feature);
  }

  return result;
}

// Map sections to their required features (if any)
// Performance/Knowledge tabs are now managed via admin settings (sidebar_custom_tabs)
export const SECTION_FEATURES: Partial<Record<string, FeatureFlag>> = {};

/**
 * Check if a section should be visible based on feature flags.
 * Sections without feature flag requirements are always visible.
 */
export function isSectionFeatureEnabled(section: string): boolean {
  const requiredFeature = SECTION_FEATURES[section];
  if (!requiredFeature) {
    return true; // No feature flag required
  }
  return isFeatureEnabled(requiredFeature);
}

/** Review gates affect authoring controls only; existing agent settings still execute. */
export interface StudioReviewFeatures {
  costs: boolean;
  routing: boolean;
  contextSummarization: boolean;
  transcriptRedaction: boolean;
  ccaasMapping: boolean;
  copilotChat: boolean;
  copilotAgentSelector: boolean;
}
export const DEFAULT_STUDIO_REVIEW_FEATURES: StudioReviewFeatures = {
  costs: false, routing: false, contextSummarization: false, transcriptRedaction: false,
  ccaasMapping: false, copilotChat: false, copilotAgentSelector: false,
};
export function getStudioReviewFeaturesServer(): StudioReviewFeatures {
  return {
    costs: isCostEstimatesEnabledServer(),
    routing: isTruthy(process.env.FEATURE_STUDIO_ROUTING, false),
    contextSummarization: isTruthy(process.env.FEATURE_STUDIO_CONTEXT_SUMMARIZATION, false),
    transcriptRedaction: isTruthy(process.env.FEATURE_STUDIO_TRANSCRIPT_REDACTION, false),
    ccaasMapping: isTruthy(process.env.FEATURE_STUDIO_CCAAS_MAPPING, false),
    copilotChat: isTruthy(process.env.FEATURE_STUDIO_COPILOT_CHAT, false),
    copilotAgentSelector: isTruthy(process.env.FEATURE_STUDIO_COPILOT_AGENT_SELECTOR, false),
  };
}
